import React, { useState } from 'react';
import { CheckboxRow, Link, Modal, PillButton, Row, Stack, Text, Title } from 'library/ui';
import { ShiftProviderUser } from 'models';
import { client, consoleLogInDev, extractErrorMessage, useClient } from 'shared';
import RevealModal from '../ShiftCard/RevealModal';

const CLEARINGHOUSE_LINK = 'https://crw.flclearinghouse.com/';

export default function ClearinghouseModal({
    provider_user,
    business_location_id,
    refresh,
    onClose,
}: {
    provider_user: ShiftProviderUser;
    business_location_id: number;
    refresh: () => void;
    onClose: () => void;
}) {
    const [revealOpen, setRevealOpen] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const { loading, send } = useClient({
        url: 'api/staff/record-action/',
        options: { sendImmediately: false },
    });

    const fullName = `${provider_user.first_name} ${provider_user.last_name}`;

    function onComplete() {
        send({
            method: 'POST',
            body: {
                action: 'add_to_clearinghouse',
                provider_user_id: provider_user.id,
                business_location_id,
            },
        })
            .then(() => refresh())
            .catch((e) => {
                alert('An error occurred. Please try again. If this persists please reach out to support@join-tandem.com.');
                consoleLogInDev(e);
            })
            .finally(() => onClose());
    }

    const onSendCode = async () => {
        try {
            setRevealOpen(true);
            await client(`api/business/reveal-code/`, { method: 'POST', body: { provider: provider_user?.id } });
        } catch (error) {
            setRevealOpen(false);
            alert(extractErrorMessage(error, 'Cannot send SSN reveal code'));
        }
    };

    return (
        <Modal onClose={onClose} width={560}>
            <Stack gap="lg">
                <Stack gap="sm">
                    <Title size="h2">Add {fullName} to Clearinghouse</Title>
                    <Text tone="secondary" lineHeight="relaxed">
                        Use the information below to add {fullName} to your roster in the DCF Background Screening{' '}
                        <Link href={CLEARINGHOUSE_LINK}>Clearinghouse</Link>, then mark this step complete once
                        they&apos;ve been added.
                    </Text>
                </Stack>
                <Row>
                    <PillButton variant="secondary" onClick={() => onSendCode()}>
                        View Worker Information
                    </PillButton>
                </Row>
                <RevealModal open={revealOpen} onClose={() => setRevealOpen(false)} provider={provider_user} minimal />
                <CheckboxRow
                    checked={confirmed}
                    onChange={setConfirmed}
                    label={`I've added ${provider_user.first_name} to my Clearinghouse roster`}
                />
                <Row gap="sm" justify="end">
                    <PillButton variant="secondary" onClick={onClose}>
                        Cancel
                    </PillButton>
                    <PillButton disabled={!confirmed || loading} onClick={onComplete}>
                        I completed this
                    </PillButton>
                </Row>
            </Stack>
        </Modal>
    );
}
